import { OrganizationUserAccessTypes } from '@generated/graphql';
import type { Affiliation } from '@interfaces/affiliation';
import type { SxProps } from '@mui/material';
import { HelpText } from '@ovComponents/2-component/helpText/helpText';
import { useHelpTextTokens } from '@ovComponents/2-component/helpText/helpText.tokens';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { Menu } from '@ovComponents/2-component/menu/menu';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { ConvertAffiliate } from '@ovComponents/4-module/workflowCompletion/subSteps/editAccount/components/convertAffiliate';
import { UserContext } from '@providers/userContextProvider';
import { Box, IconButton, Typography, useTheme } from 'hephaestus/web';
import {
  Add,
  ArrowForwardIosSharp,
  CheckCircle,
  Delete as DeleteIcon,
  Edit,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  RemoveCircleOutlined,
} from 'hephaestus/webIcons';
import { kebabCase } from 'lodash-es';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermission } from '../../../hooks/usePermission';
import { useSelectionTileTokens } from '../selectionTile/selectionTile.tokens';

type CompletionBoxState = 'todo' | 'complete' | 'error' | 'next';
type CompletionBoxType = 'checkbox' | 'radio' | 'tile' | 'completion';
type CompletionBoxVariant = 'edit' | 'view';

const completionBoxOnClick = (
  state: CompletionBoxState,
  variant: CompletionBoxVariant,
  disabled: boolean,
  onAdd?: () => void,
) =>
  ((state === 'todo' && variant === 'edit') || state === 'next') && !disabled
    ? onAdd
    : undefined;

export const CompletionBox = ({
  state,
  title,
  titleSideComponent,
  onRemove,
  onAdd,
  onEdit,
  children,
  variant = 'edit',
  edittable = true,
  selected,
  infoTooltip,
  type,
  testId,
  error,
  errorText,
  affiliate,
  options,
  accountData,
  refetch,
  disabled = false,
  sx,
  innerSx,
}: {
  state: CompletionBoxState;
  title: string;
  titleSideComponent?: React.ReactNode;
  onRemove?: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  children?: any;
  variant?: CompletionBoxVariant;
  edittable?: boolean;
  type?: CompletionBoxType;
  selected?: boolean;
  infoTooltip?: string;
  testId?: string;
  error?: boolean;
  errorText?: string;
  options?: any;
  accountData?: any;
  refetch?: () => void;
  affiliate?: Affiliation;
  disabled?: boolean;
  sx?: SxProps;
  innerSx?: SxProps;
}) => {
  const theme = useTheme();
  const tokens = useSelectionTileTokens(theme);
  const helpTextTokens = useHelpTextTokens(theme);
  const { t } = useTranslation(['components']);
  const canWriteAffiliateConversion = usePermission(
    'write:affiliate_conversion',
  );
  const { userContext } = useContext(UserContext);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openConvertAffiliate, setOpenConvertAffiliate] = useState(false);

  const shouldDisplayInDialog = helpTextTokens.displayInDialog;

  const open = Boolean(anchorEl);

  // Invite user menu will visible only for OrganizationUserAccessTypes.Organization users
  const canCovertAffiliate =
    affiliate &&
    canWriteAffiliateConversion &&
    affiliate?.user?.affiliateOnly &&
    userContext.accessType === OrganizationUserAccessTypes.Organization &&
    options?.enableConvertedAffiiateOnboarding;

  const helpTextFunc = (isError?: boolean) => {
    if (isError) {
      return errorText;
    }
    return infoTooltip && !shouldDisplayInDialog ? infoTooltip : undefined;
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const borderColor = () => {
    if (error) return theme.color.negative;
    if (variant === 'view') return undefined;
    switch (state) {
      case 'todo':
        return theme.color.outlineVariant;
      case 'complete':
        return theme.color.positive;
      case 'error':
        return theme.color.negative;
      default:
        return theme.color.outlineVariant;
    }
  };

  const displayMenuOptions =
    edittable && accountData && affiliate && ['complete'].includes(state);
  const isInteractive = variant === 'edit' && !disabled;

  return (
    <>
      <Box
        data-testid={
          testId ? `${testId}-${kebabCase(title)}` : 'completion-box'
        }
        onClick={completionBoxOnClick(state, variant, disabled, onAdd)}
        sx={
          {
            display: 'flex',
            borderRadius: theme.borderRadius.xl,
            border: `1px solid ${borderColor()}`,
            pt: 1,
            pl: 3,
            pr: 2,
            pb: 2,
            flexDirection: 'column',
            mb: 2,
            transition: 'background-color 0.3s ease-in-out',

            cursor:
              (state === 'todo' || type === 'completion') && isInteractive
                ? 'pointer'
                : 'default',

            '&:hover': {
              backgroundColor:
                (state === 'todo' || type === 'completion') && isInteractive
                  ? theme.color.background
                  : 'default',
            },

            opacity: disabled ? 0.5 : undefined,

            background: selected
              ? tokens.hoverBackgroundColor
              : theme.color.surface,

            boxShadow: variant === 'view' ? theme.elevation.surface : undefined,
            ...sx,
          } as any
        }
      >
        <Box
          sx={
            [
              {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                minHeight: 40,
              },
              ...(Array.isArray(innerSx) ? innerSx : [innerSx]),
            ] as any
          }
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <Typography
                data-testid={
                  testId
                    ? `${testId}-title-${kebabCase(title)}`
                    : `completion-box-title-${kebabCase(title)}`
                }
                variant='titleMedium'
                sx={{ mr: '4px' }}
              >
                {title}
                {infoTooltip && shouldDisplayInDialog && (
                  <InfoDialog information={infoTooltip} />
                )}
              </Typography>
              {titleSideComponent}
            </Box>
            {state === 'complete' && (
              <CheckCircle
                sx={
                  {
                    color: theme.color.positive,
                    fontSize: '16px',
                    ml: 0.5,
                  } as any
                }
              />
            )}
          </Box>
          <Box>
            {state === 'todo' && edittable && (
              <IconButton
                aria-label='add'
                disableRipple={variant === 'edit'}
                onClick={variant === 'edit' ? undefined : onAdd}
                size='medium'
              >
                <Add
                  data-testid={testId ? `${testId}-add-button` : 'add-button'}
                  sx={{ color: theme.color.onSurfaceVariant } as any}
                  fontSize='inherit'
                />
              </IconButton>
            )}
            {!accountData && state === 'complete' && onEdit && edittable && (
              <IconButton
                aria-label='edit'
                onClick={onEdit}
                size='xsmall'
                sx={{ height: '26px' }}
              >
                <Edit
                  data-testid={testId ? `${testId}-edit-button` : 'edit-button'}
                  sx={{ fontSize: '16px' }}
                  fontSize='inherit'
                />
              </IconButton>
            )}
            {!accountData && state === 'complete' && onRemove && edittable && (
              <IconButton
                aria-label='remove'
                onClick={onRemove}
                size='xsmall'
                sx={{ height: '26px' }}
              >
                <RemoveCircleOutlined
                  data-testid={
                    testId ? `${testId}-remove-button` : 'remove-button'
                  }
                  sx={{ fontSize: '16px' }}
                  fontSize='inherit'
                />
              </IconButton>
            )}
            {displayMenuOptions && (
              <IconButton
                aria-label='more actions'
                sx={{ marginLeft: 1 }}
                size='xsmall'
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick(e);
                }}
              >
                <MoreVertIcon fontSize='inherit' />
              </IconButton>
            )}
            {state === 'next' && (
              <ArrowForwardIosSharp
                data-testid={testId ? `${testId}-next-button` : 'next-button'}
                sx={
                  {
                    fontSize: '16px',
                    color: theme.color.onSurfaceVariant,
                  } as any
                }
              />
            )}
          </Box>
        </Box>
        {children}
      </Box>
      {helpTextFunc(error) && (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            position: 'relative',
            top: '-10px',
            mt: 0.5,
          }}
        >
          <HelpText
            text={helpTextFunc(error)}
            tone={error ? 'error' : 'neutral'}
          />
        </Box>
      )}

      <Menu
        anchorEl={anchorEl}
        id='affiliate-menu'
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {displayMenuOptions && onEdit && (
          <MenuItem
            data-testid={testId ? `${testId}-edit-button` : 'edit-button'}
            key='edit-menu-item'
            onClick={onEdit}
          >
            <IconButton aria-label='edit' size='xsmall' sx={{ marginRight: 1 }}>
              <EditIcon fontSize='inherit' />
            </IconButton>
            {t('affiliateConversion.edit')}
          </MenuItem>
        )}

        {displayMenuOptions && onRemove && (
          <MenuItem
            data-testid={testId ? `${testId}-remove-button` : 'remove-button'}
            key='delete-menu-item'
            onClick={onRemove}
          >
            <IconButton
              aria-label='delete'
              size='xsmall'
              sx={{ marginRight: 1 }}
            >
              <DeleteIcon fontSize='inherit' />
            </IconButton>
            {t('affiliateConversion.delete')}
          </MenuItem>
        )}

        {canCovertAffiliate && (
          <MenuItem
            key='invite'
            data-testid='affiliate-invite'
            onClick={() => {
              setOpenConvertAffiliate(true);
            }}
          >
            <IconButton
              aria-label='person'
              size='xsmall'
              sx={{ marginRight: 1 }}
            >
              <PersonIcon fontSize='inherit' />
            </IconButton>
            {t('affiliateConversion.inviteUser')}
          </MenuItem>
        )}
      </Menu>
      {displayMenuOptions && (
        <ConvertAffiliate
          refetch={refetch}
          options={options}
          accountData={accountData}
          setOpenConvertAffiliate={setOpenConvertAffiliate}
          openConvertAffiliate={openConvertAffiliate}
          currentAffiliate={affiliate}
        />
      )}
    </>
  );
};
